import { codelistStore } from './codelist-store';
import { Code, ColorSchema, RGBAColor, RgbaColorSchema, StringLineStyle, SurfaceProperties, SurfacePropertiesSchema } from './types';
import { ZodSafeParseResult } from 'zod';

const defaultSurfaceColor: RGBAColor = {
    r: 117,
    g: 191,
    b: 255,
    a: 1,
};

const defaultPointColor: RGBAColor = {
    r: 0,
    g: 0,
    b: 0,
    a: 1,
};

const defaultLineColor: RGBAColor = {
    r: 0,
    g: 0,
    b: 0,
    a: 1,
};

const defaultPointStyle = 'dot';

const getSurfaceColorFromPropertyData = (metadata: unknown): RGBAColor => {
    // Check for direct color property first (from mesh color data)
    if (metadata && typeof metadata === 'object' && 'color' in metadata && typeof metadata.color === 'string') {
        const colorParts = metadata.color.split(',');
        if (colorParts.length === 3) {
            const r = parseInt(colorParts[0], 10);
            const g = parseInt(colorParts[1], 10);
            const b = parseInt(colorParts[2], 10);
            if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
                return { r, g, b, a: 1 };
            }
        }
    }

    const surfacePropertiesParseResult: ZodSafeParseResult<SurfaceProperties> = SurfacePropertiesSchema.safeParse(metadata);

    if (!surfacePropertiesParseResult.success) {
        console.warn('Failed to parse surface properties from metadata.', surfacePropertiesParseResult.error.issues);
        console.warn('Invalid surface properties metadata:', metadata);
        console.warn('Expected format:', SurfacePropertiesSchema);
        console.warn('Skipping color assignment for this feature. Returning default color (blue).');
        return defaultSurfaceColor;
    }

    const surfaceProperties: SurfaceProperties = surfacePropertiesParseResult.data;
    const key: string = surfaceProperties.infraCode || surfaceProperties.name;
    const code: Code | undefined = codelistStore.getCodeByName(key);

    if (!code) {
        console.warn(`No code found for infraCode: ${surfaceProperties.infraCode}`);
        return defaultSurfaceColor;
    }

    return getSurfaceColorForCode(code);
};

const getSurfaceColorForInfraCode = (infraCode: string, fallbackColor: RGBAColor = defaultSurfaceColor): RGBAColor => {
    const code: Code | undefined = codelistStore.getCodeByName(infraCode);

    if (!code) {
        return fallbackColor;
    }

    return getSurfaceColorForCode(code);
};

const getPointColorFromOutlinePoint = (point: IOutlinePoint): RGBAColor => {
    const key: string = point.code || point.pointName || '';
    const code: Code | undefined = codelistStore.getCodeByName(key);

    if (!code) {
        return defaultPointColor;
    }

    return getPointClorForCode(code);
};

const getPointStyleFromOutlinePoint = (point: IOutlinePoint) => {
    const key: string = point.code || point.pointName || '';
    const code: Code | undefined = codelistStore.getCodeByName(key);

    if (!code || !code.pointstyle) {
        return defaultPointStyle;
    }

    return code.pointstyle;
};

const getPipeColorFromPipePoint = (point: IPipePoint): RGBAColor => {
    const key: string = point.props?.infraCoding || '';
    const code: Code | undefined = codelistStore.getCodeByName(key);

    if (!code) {
        return defaultPointColor;
    }

    return getPointClorForCode(code);
};

const getPipeColorFromStringLine = (stringline: IOutlinePolyline): RGBAColor => {
    const key: string = stringline.props?.infraCoding || '';
    const code: Code | undefined = codelistStore.getCodeByName(key);

    if (!code) {
        return defaultLineColor;
    }

    if (code.linecolor) {
        return getLineColorForCode(code);
    }

    if (code.pointcolor) {
        return getPointClorForCode(code);
    }

    if (code.surfacecolor) {
        return getSurfaceColorForCode(code);
    }

    return defaultLineColor;
};

const getPipeColorFromInfraCode = (infraCode: string, fallbackColor: RGBAColor = defaultLineColor): RGBAColor => {
    const code: Code | undefined = codelistStore.getCodeByName(infraCode);

    if (!code) {
        return fallbackColor;
    }

    if (code.linecolor) {
        return getLineColorForCode(code);
    }

    if (code.pointcolor) {
        return getPointClorForCode(code);
    }

    if (code.surfacecolor) {
        return getSurfaceColorForCode(code);
    }

    return defaultLineColor;
};

const getLineColorFromStringLine = (stringline: IOutlinePolyline): RGBAColor => {
    const key: string = stringline.props?.lineCode || stringline.props?.infraCoding || stringline.props?.terrainCoding || '';
    const code: Code | undefined = codelistStore.getCodeByName(key);

    if (!code) {
        return defaultLineColor;
    }
    return getLineColorForCode(code);
};

const getLineStyleFromStringLine = (stringline: IOutlinePolyline): StringLineStyle => {
    const key: string = stringline.props?.lineCode || stringline.props?.infraCoding || stringline.props?.terrainCoding || '';
    const code: Code | undefined = codelistStore.getCodeByName(key);

    if (!code) {
        return 'solid';
    }
    return code.linestyle ? StringLineStyle.parse(code.linestyle) : 'solid';
};

const getLineColorForCode = (code: Code): RGBAColor => {
    const colorData: string | undefined = code.linecolor;
    const colorParseResult: ZodSafeParseResult<string> = ColorSchema.safeParse(colorData);
    if (!colorParseResult.success) {
        console.warn(`Invalid color format for code ${code.name}: ${colorData}`);
        return defaultLineColor;
    }

    return RgbaColorSchema.parse(colorParseResult.data);
};

const getPointClorForCode = (code: Code): RGBAColor => {
    const colorData = code.pointcolor || code.linecolor || code.surfacecolor;
    const colorParseResult: ZodSafeParseResult<string> = ColorSchema.safeParse(colorData);
    if (!colorParseResult.success) {
        console.warn(`Invalid color format for code ${code.name}: ${colorData}`);
        return defaultPointColor;
    }

    return RgbaColorSchema.parse(colorParseResult.data);
};

const getSurfaceColorForCode = (code: Code): RGBAColor => {
    const colorParseResult: ZodSafeParseResult<string> = ColorSchema.safeParse(code.surfacecolor);
    if (!colorParseResult.success) {
        console.warn(`Invalid color format for code ${code.name}: ${code.surfacecolor}`);
        return defaultSurfaceColor;
    }

    return RgbaColorSchema.parse(colorParseResult.data);
};

const getPointColorForCode = (codeProperty: string, fallbackColor: RGBAColor = defaultPointColor) => {
    const code: Code | undefined = codelistStore.getCodeByName(codeProperty);
    if (!code) {
        return fallbackColor;
    }
    return getPointClorForCode(code);
};

export const ColorService = {
    getSurfaceColorFromPropertyData,
    getSurfaceColorForInfraCode,
    getPipeColorFromInfraCode,
    getPointColorFromOutlinePoint,
    getLineColorFromStringLine,
    getLineStyleFromStringLine,
    getPipeColorFromStringLine,
    getPipeColorFromPipePoint,
    getPointStyleFromOutlinePoint,
    getPointColorForCode,
};
